import type { ComplianceDocumentSectionRecord } from '@artem-lex/compliance-documents'
import type { Editor } from '@tiptap/core'

export function reindexSections<T extends ComplianceDocumentSectionRecord>(
  sections: T[]
): T[] {
  return sections.map((section, index) => ({
    ...section,
    sortOrder: index,
  }))
}

export function createCustomAppendixSection<
  T extends ComplianceDocumentSectionRecord,
>(existingSections: T[], title: string): T {
  let suffix =
    existingSections.filter((section) => section.kind === 'custom').length + 1
  let sectionId = `appendix-${suffix}`
  while (existingSections.some((section) => section.sectionId === sectionId)) {
    suffix += 1
    sectionId = `appendix-${suffix}`
  }
  return {
    sectionId,
    title,
    kind: 'custom',
    sortOrder: existingSections.length,
    markdown: '',
  } as T
}

export function readEditorMarkdown(editor: Editor | null | undefined): string {
  if (editor == null) {
    return ''
  }
  const editorWithMarkdown = editor as Editor & {
    getMarkdown?: () => string
    markdown?: { serialize: (json: unknown) => string }
  }
  if (typeof editorWithMarkdown.getMarkdown === 'function') {
    return editorWithMarkdown.getMarkdown()
  }
  if (editorWithMarkdown.markdown != null) {
    return editorWithMarkdown.markdown.serialize(editor.getJSON())
  }
  return ''
}

export function readEditorSelectionMarkdown(
  editor: Editor,
  from: number,
  to: number
): string {
  const editorWithMarkdown = editor as Editor & {
    markdown?: { serialize: (json: unknown) => string }
  }

  if (editorWithMarkdown.markdown != null) {
    const slice = editor.state.doc.slice(from, to)
    const sliceDoc = {
      type: 'doc',
      content: slice.content.toJSON(),
    }
    return editorWithMarkdown.markdown.serialize(sliceDoc)
  }

  return editor.state.doc.textBetween(from, to, '\n')
}

function isSectionNumber(sectionId: string): boolean {
  return /^\d+(\.\d+)*$/.test(sectionId)
}

export function getSectionHeadingLabel(
  section: ComplianceDocumentSectionRecord,
  sectionTitle: string
): string {
  if (section.kind === 'custom') {
    if (sectionTitle.length > 0) {
      return sectionTitle
    }
    return section.sectionId
  }
  if (sectionTitle.length > 0) {
    if (!isSectionNumber(section.sectionId)) {
      return sectionTitle
    }
    return `${section.sectionId} ${sectionTitle}`
  }
  return section.sectionId
}
